import React from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress 
} from '@mui/material';
import { 
  Timeline, 
  TimelineItem, 
  TimelineSeparator, 
  TimelineConnector, 
  TimelineContent, 
  TimelineDot 
} from '@mui/lab';
import { 
  Add, 
  Assignment, 
  CheckCircle, 
  Schedule,
  Info,
  Pending,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserApplications } from '../../hooks/useApplications';
import LoadingOverlay from '../Shared/LoadingOverlay';

const StatCard = ({ title, value, icon, color, onClick }) => (
  <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div" color={color}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: color, fontSize: 40 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const CitizenDashboard = () => {
  const navigate = useNavigate();
  
  // Fetch user's applications
  const { 
    data: userApplications, 
    isLoading, 
    error 
  } = useUserApplications();

  // Extract applications array from paginated response
  const applications = userApplications?.applications || [];

  // Calculate statistics
  const stats = {
    total: applications.length,
    pending: applications.filter(app => ['SUBMITTED', 'UNDER_REVIEW', 'PENDING_INSPECTION'].includes(app.status)).length,
    approved: applications.filter(app => app.status === 'APPROVED').length,
    completed: applications.filter(app => app.status === 'PAYMENT_PROCESSED').length,
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'PAYMENT_PROCESSED':
        return <CheckCircle />;
      case 'REJECTED':
        return <ErrorIcon />;
      case 'UNDER_REVIEW':
      case 'PENDING_INSPECTION':
        return <Pending />;
      default:
        return <Schedule />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'PAYMENT_PROCESSED':
        return 'success.main';
      case 'REJECTED':
        return 'error.main';
      case 'UNDER_REVIEW':
      case 'PENDING_INSPECTION':
        return 'warning.main';
      default:
        return 'info.main';
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Error loading your applications: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Citizen Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        Track your ex-gratia applications and apply for assistance
      </Typography>

      {/* Citizen Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Applications"
            value={isLoading ? <CircularProgress size={24} /> : stats.total}
            icon={<Assignment />}
            color="primary.main"
            onClick={() => navigate('/applications')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Approved"
            value={isLoading ? <CircularProgress size={24} /> : stats.approved}
            icon={<CheckCircle />}
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Review"
            value={isLoading ? <CircularProgress size={24} /> : stats.pending}
            icon={<Schedule />}
            color="warning.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Payment Processed"
            value={isLoading ? <CircularProgress size={24} /> : stats.completed}
            icon={<CheckCircle />}
            color="primary.main"
          />
        </Grid>
      </Grid>

      {/* Main Actions */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Applications
              </Typography>
              <Timeline>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                  </Box>
                ) : applications.length === 0 ? (
                  <Box textAlign="center" p={3}>
                    <Typography variant="body1" color="text.secondary">
                      No applications found
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Add />}
                      onClick={() => navigate('/applications/new')}
                      sx={{ mt: 2 }}
                    >
                      Submit Your First Application
                    </Button>
                  </Box>
                ) : (
                  applications.slice(0, 3).map((app, index) => (
                    <TimelineItem key={app.application_refno || app.id}>
                      <TimelineSeparator>
                        <TimelineDot sx={{ color: getStatusColor(app.status) }}>
                          {getStatusIcon(app.status)}
                        </TimelineDot>
                        {index < Math.min(applications.length - 1, 2) && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent>
                        <Typography variant="h6" component="span">
                          {app.damage_type} - {app.status || 'SUBMITTED'}
                        </Typography>
                        <Typography color="text.secondary">
                          {app.application_refno || app.id} • {app.village}, {app.district} • 
                          {app.created_at ? new Date(app.created_at).toLocaleDateString('en-IN') : 'Recent'}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>
                  ))
                )}
              </Timeline>
              
              {!isLoading && applications.length > 0 && (
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => navigate('/applications')}
                  sx={{ mt: 2 }}
                >
                  View All My Applications ({applications.length})
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Apply for Ex-Gratia
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Apply for compensation due to natural calamity damage
              </Typography>
              <Button 
                variant="contained" 
                fullWidth
                startIcon={<Add />}
                onClick={() => navigate('/applications/new')}
              >
                New Application
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Info sx={{ mr: 1 }} />
                Important Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Ensure all documents are uploaded clearly
                • Field inspection may be required
                • Processing time: 15-30 days
                • Contact helpline: 1800-XXX-XXXX
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loading Overlay */}
      <LoadingOverlay
        open={isLoading}
        message="Loading dashboard data..."
      />
    </Box>
  );
};

export default CitizenDashboard;
